/**
* RelytArt
*
* Creative Commons Attribution-NonCommercial-NoDerivatives 4.0 International
* Copyright (c) 2022 RelytArt
*
* License: https://docs.google.com/document/d/1UD7jhCYWRXSxhx4A5vMeGVTjujwLM39BmvjwA0eFFfk/edit?usp=sharing
*
*/

// errors
import ApiRequestFailError from "errors/ApiRequestFailError.js";

// constants
const API_ROOT = String("https://us-central1-relytart-66f71.cloudfunctions.net");
// const API_ROOT = String("http://localhost:5001/relytart-66f71/us-central1");

class FirebaseCloudFunctions {

  /*====================
  getters
  ====================*/
  /**
  * @returns {Object}
  */
  static getApiPaths() {
    return {
      application: String(`${API_ROOT}/application`),
      auth: String(`${API_ROOT}/auth`),
      collections: String(`${API_ROOT}/collections`),
      contact: String(`${API_ROOT}/contact`),
      items: String(`${API_ROOT}/items`),
      qrcodes: String(`${API_ROOT}/qrcodes`),
    };
  }

  /**
  * @returns {Object}
  */
  static getHeaders() {
    return {
      "Accept": "application/json;text/plain",
      "Cache-Control": "no-cache",
      "Content-Type": "application/json",
    };
  }

  /**
  * @returns {Number[]}
  */
  static getSuccessStatuses() {
    return [
      Number("200"),
      Number("201"),
      Number("202"),
      Number("203"),
      Number("204"),
      Number("205"),
      Number("206")
    ];
  }

  /*====================
  orchestration
  ====================*/
  /**
  * @param {String} url
  * @param {Object} body
  * @returns {Promise<Object | String>}
  * @throws {ApiRequestFailError}
  */
  static async _private_fetch(url, body) {

    let response;
    try {
      response = await fetch(url, {
        body: JSON.stringify(body),
        headers: FirebaseCloudFunctions.getHeaders(),
        method: String("POST"),
      });
    }
    catch(error) {
      throw new ApiRequestFailError(error.message);
    }

    let data;
    const successStatuses = FirebaseCloudFunctions.getSuccessStatuses();
    if(successStatuses.includes(response.status)) {
      const contentType = response.headers.get("content-type");
      if (contentType === null) {
        throw new ApiRequestFailError(`Invalid response metadata content-type: "null".`);
      }
      else if (contentType.startsWith("application/json")){
        data = await response.json();
      }
      else if (contentType.startsWith("text/plain")) {
        data = await response.text();
      }
      else {
        throw new ApiRequestFailError(`Unsupported response content-type: ${contentType}`);
      }
    }
    else {
      let errorMessage;
      try { errorMessage = await response.text(); }
      catch { /* ignore error */ }
      if(errorMessage) {
        throw new ApiRequestFailError(`[${response.status}]: ${response.statusText}. ${errorMessage}`);
      }
      else {
        throw new ApiRequestFailError(`[${response.status}]: ${response.statusText}.`);
      }
    }
    return data;
  }

  /**
  * @param {String} apiPath
  * @param {String} domain
  * @param {Object} payload
  * @param {String} userIdentityToken
  * @returns {Promise<Object | String>}
  * @throws {ApiRequestFailError}
  */
  static async apiRequest(apiPath, domain, payload, userIdentityToken) {
    const body = { domain, payload, userIdentityToken };
    return FirebaseCloudFunctions._private_fetch(apiPath, body);
  }

  /**
  * @param {String} domain
  * @param {String} message
  * @param {String} type
  * @returns {Promise<Object | String>}
  * @throws {ApiRequestFailError}
  */
  static async contact(domain, message, type) {
    const { contact: apiPath } = FirebaseCloudFunctions.getApiPaths();
    const body = { domain, message, type };
    return FirebaseCloudFunctions._private_fetch(apiPath, body);
  }

  /**
  * @param {String} domain
  * @param {String} userIdentityToken
  * @returns {Promise<Object | String>}
  * @throws {ApiRequestFailError}
  */
  static async validateUserPrivilege(domain, userIdentityToken) {
    const { auth: apiPath } = FirebaseCloudFunctions.getApiPaths();
    const body = { domain, userIdentityToken };
    return FirebaseCloudFunctions._private_fetch(apiPath, body);
  }
}
export default FirebaseCloudFunctions;
